import { useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet, View } from "react-native";

import {
  AppText,
  Button,
  ButtonText,
  Card,
  DetailRow,
  EmptyState,
  ProtectedScreen,
  SectionHeader,
  Stack,
  TextField,
} from "~/components/ui";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceInvoices,
  useWorkspaceProjects,
  useWorkspaceTasks,
  useWorkspaceTimeEntries,
} from "~/hooks/workspace";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import {
  TASK_PRIORITY_OPTIONS,
  taskPriorityLabel,
} from "~/utils/work-planning";

function normalizeParam(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value;
}

function formatMoney(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatHours(seconds: number) {
  const hours = seconds / 3600;
  return `${hours.toFixed(hours >= 10 ? 0 : 1)}h`;
}

function billableValue(entry: {
  duration_seconds: number;
  hourly_rate_cents: number | null;
}) {
  if (!entry.hourly_rate_cents) return 0;
  return Math.round((entry.duration_seconds / 3600) * entry.hourly_rate_cents);
}

function dueLabel(dueOn: string | null, status: string) {
  if (!dueOn) return "No due date";
  const today = new Date().toISOString().slice(0, 10);
  if (dueOn < today && status !== "done" && status !== "archived") {
    return `Overdue ${dueOn}`;
  }
  return `Due ${dueOn}`;
}

const TASK_STATUS_ACTIONS = [
  { label: "To do", value: "todo" },
  { label: "Doing", value: "doing" },
  { label: "Done", value: "done" },
  { label: "Archive", value: "archived" },
] as const;

export default function TaskDetailScreen() {
  const stylesheet = useThemedStyles(makeStyles);
  const router = useRouter();
  const params = useLocalSearchParams<{ id?: string | string[] }>();
  const taskId = normalizeParam(params.id);
  const [timeDescription, setTimeDescription] = useState("");
  const [timeMinutes, setTimeMinutes] = useState("60");
  const [timeHourlyRate, setTimeHourlyRate] = useState("");
  const workspace = usePrimaryWorkspace();
  const clients = useWorkspaceClients(workspace.data?.id);
  const projects = useWorkspaceProjects(workspace.data?.id);
  const tasks = useWorkspaceTasks(workspace.data?.id);
  const timeEntries = useWorkspaceTimeEntries(workspace.data?.id);
  const invoices = useWorkspaceInvoices(workspace.data?.id);

  const task =
    (tasks.data ?? []).find((record) => record.id === taskId) ?? null;
  const project = task?.project_id
    ? (projects.data ?? []).find((record) => record.id === task.project_id)
    : null;
  const client = project?.client_id
    ? (clients.data ?? []).find((record) => record.id === project.client_id)
    : null;
  const taskTimeEntries = (timeEntries.data ?? []).filter(
    (entry) => entry.task_id === task?.id,
  );
  const invoiceIds = new Set(
    taskTimeEntries
      .map((entry) => entry.invoice_id)
      .filter((invoiceId): invoiceId is string => Boolean(invoiceId)),
  );
  const relatedInvoices = (invoices.data ?? []).filter((invoice) =>
    invoiceIds.has(invoice.id),
  );
  const trackedSeconds = taskTimeEntries.reduce(
    (total, entry) => total + entry.duration_seconds,
    0,
  );
  const billableSeconds = taskTimeEntries
    .filter((entry) => entry.billable)
    .reduce((total, entry) => total + entry.duration_seconds, 0);
  const invoicedSeconds = taskTimeEntries
    .filter((entry) => entry.invoiced_at)
    .reduce((total, entry) => total + entry.duration_seconds, 0);
  const unbilledCents = taskTimeEntries
    .filter((entry) => entry.billable && !entry.invoiced_at)
    .reduce((total, entry) => total + billableValue(entry), 0);
  const invoicedCents = taskTimeEntries
    .filter((entry) => entry.invoiced_at)
    .reduce((total, entry) => total + billableValue(entry), 0);
  const error =
    workspace.error ??
    clients.error ??
    projects.error ??
    tasks.error ??
    tasks.updatePriority.error ??
    tasks.updateStatus.error ??
    tasks.setBillable.error ??
    timeEntries.error ??
    timeEntries.create.error ??
    invoices.error;
  const errorMessage = error instanceof Error ? error.message : "";

  async function createTaskTimeEntry() {
    if (!task || !timeDescription.trim()) return;
    await timeEntries.create.mutateAsync({
      billable: task.billable ? "true" : "false",
      clientId: project?.client_id ?? "",
      description: timeDescription,
      hourlyRate: task.billable ? timeHourlyRate.trim() : "",
      minutes: timeMinutes,
      projectId: task.project_id ?? "",
      taskId: task.id,
    });
    setTimeDescription("");
    setTimeMinutes("60");
    setTimeHourlyRate("");
  }

  if (workspace.isLoading || tasks.isLoading) {
    return (
      <ProtectedScreen title="Task" subtitle="Loading task details.">
        <Card gap="sm">
          <AppText>Loading...</AppText>
        </Card>
      </ProtectedScreen>
    );
  }

  if (!task) {
    return (
      <ProtectedScreen title="Task not found">
        <Card gap="md">
          <AppText size="sm">
            This task could not be found in the current workspace.
          </AppText>
          <Button
            align="start"
            variant="outline"
            onPress={() => router.push("/tasks" as never)}
          >
            <ButtonText>Back to tasks</ButtonText>
          </Button>
        </Card>
      </ProtectedScreen>
    );
  }

  return (
    <ProtectedScreen
      title={task.name}
      subtitle="Review task priority, delivery status, tracked work, and billable follow-through."
    >
      {errorMessage ? (
        <Card danger gap="xs">
          <AppText tone="danger" weight="semibold">
            Task details could not load
          </AppText>
          <AppText tone="danger" size="xs">
            {errorMessage}
          </AppText>
        </Card>
      ) : null}

      <View style={stylesheet.summaryGrid}>
        <Card gap="xs">
          <AppText size="xs">Status</AppText>
          <AppText size="lg" weight="bold">
            {task.status}
          </AppText>
        </Card>
        <Card gap="xs">
          <AppText size="xs">Priority</AppText>
          <AppText size="lg" weight="bold">
            {taskPriorityLabel(task.priority)}
          </AppText>
        </Card>
        <Card gap="xs">
          <AppText size="xs">Due</AppText>
          <AppText size="lg" weight="bold">
            {dueLabel(task.due_on, task.status)}
          </AppText>
        </Card>
        <Card gap="xs">
          <AppText size="xs">Tracked</AppText>
          <AppText size="lg" weight="bold">
            {formatHours(trackedSeconds)}
          </AppText>
        </Card>
        <Card gap="xs">
          <AppText size="xs">Unbilled</AppText>
          <AppText size="lg" weight="bold">
            {formatMoney(unbilledCents)}
          </AppText>
        </Card>
      </View>

      <Card gap="md">
        <SectionHeader
          title="Task actions"
          description="Move this task, change billing intent, and record work without leaving the task."
        />
        <View style={stylesheet.actions}>
          {TASK_STATUS_ACTIONS.map((status) => (
            <Button
              key={status.value}
              compact
              variant={task.status === status.value ? "primary" : "outline"}
              disabled={tasks.updateStatus.isPending}
              onPress={() =>
                void tasks.updateStatus.mutateAsync({
                  id: task.id,
                  status: status.value,
                })
              }
            >
              <ButtonText>{status.label}</ButtonText>
            </Button>
          ))}
          {TASK_PRIORITY_OPTIONS.map((priority) => (
            <Button
              key={priority.value}
              compact
              variant={task.priority === priority.value ? "primary" : "outline"}
              disabled={tasks.updatePriority.isPending}
              onPress={() =>
                void tasks.updatePriority.mutateAsync({
                  id: task.id,
                  priority: priority.value,
                })
              }
            >
              <ButtonText>{priority.label} priority</ButtonText>
            </Button>
          ))}
          <Button
            compact
            iconName="circle-dollar-sign"
            variant="outline"
            disabled={tasks.setBillable.isPending}
            onPress={() =>
              void tasks.setBillable.mutateAsync({
                id: task.id,
                billable: !task.billable,
              })
            }
          >
            <ButtonText>
              {task.billable ? "Make non-billable" : "Make billable"}
            </ButtonText>
          </Button>
        </View>
        <View style={stylesheet.actionPanel}>
          <SectionHeader
            title="Log task time"
            description="Save manual time directly against this task and its project."
          />
          <View style={stylesheet.formGrid}>
            <View style={[stylesheet.field, stylesheet.fullWidth]}>
              <AppText size="xs" weight="semibold">
                Description
              </AppText>
              <TextField
                value={timeDescription}
                placeholder="Implementation, review, client follow-up..."
                onChangeText={setTimeDescription}
              />
            </View>
            <View style={stylesheet.field}>
              <AppText size="xs" weight="semibold">
                Minutes
              </AppText>
              <TextField
                value={timeMinutes}
                placeholder="60"
                keyboardType="numeric"
                onChangeText={setTimeMinutes}
              />
            </View>
            <View style={stylesheet.field}>
              <AppText size="xs" weight="semibold">
                Hourly rate
              </AppText>
              <TextField
                value={timeHourlyRate}
                placeholder="Use matching preset"
                editable={task.billable}
                keyboardType="numeric"
                muted={!task.billable}
                onChangeText={setTimeHourlyRate}
              />
            </View>
          </View>
          <Button
            align="start"
            compact
            iconName="clock-plus"
            disabled={!timeDescription.trim() || timeEntries.create.isPending}
            onPress={() => void createTaskTimeEntry()}
          >
            <ButtonText>
              {timeEntries.create.isPending ? "Saving..." : "Log time"}
            </ButtonText>
          </Button>
        </View>
      </Card>

      <View style={stylesheet.detailGrid}>
        <Card gap="md">
          <SectionHeader
            title="Task context"
            description="Project, client, due date, and billing intent for this piece of work."
          />
          <Stack gap="sm">
            <DetailRow label="Project" value={project?.name ?? "No project"} />
            <DetailRow label="Client" value={client?.name ?? "No client"} />
            <DetailRow
              label="Priority"
              value={taskPriorityLabel(task.priority)}
            />
            <DetailRow label="Due date" value={task.due_on ?? "Not set"} />
            <DetailRow
              label="Billing"
              value={task.billable ? "Billable" : "Non-billable"}
            />
          </Stack>
          {task.description ? (
            <View style={stylesheet.noteBlock}>
              <AppText size="xs" weight="semibold">
                Description
              </AppText>
              <AppText size="sm">{task.description}</AppText>
            </View>
          ) : null}
        </Card>

        <Card gap="md">
          <SectionHeader
            title="Time and billing"
            description="Task-linked time, invoice status, and work still ready to bill."
          />
          <Stack gap="sm">
            <DetailRow
              label="All tracked"
              value={formatHours(trackedSeconds)}
            />
            <DetailRow
              label="Billable tracked"
              value={formatHours(billableSeconds)}
            />
            <DetailRow
              label="Invoiced tracked"
              value={formatHours(invoicedSeconds)}
            />
            <DetailRow
              label="Invoiced value"
              value={formatMoney(invoicedCents)}
            />
            <DetailRow
              label="Unbilled value"
              value={formatMoney(unbilledCents)}
            />
          </Stack>
        </Card>
      </View>

      <View style={stylesheet.detailGrid}>
        <Card gap="md">
          <SectionHeader
            title="Recent time entries"
            description="Time entries linked directly to this task."
          />
          {taskTimeEntries.length === 0 ? (
            <EmptyState title="No time has been tracked for this task." />
          ) : (
            <Stack gap="sm">
              {taskTimeEntries.slice(0, 8).map((entry) => (
                <View key={entry.id} style={stylesheet.itemRow}>
                  <View style={stylesheet.itemMain}>
                    <AppText weight="semibold">
                      {entry.description ?? "Tracked time"}
                    </AppText>
                    <AppText size="xs">
                      {formatHours(entry.duration_seconds)} -{" "}
                      {entry.invoiced_at ? "Invoiced" : "Unbilled"}
                    </AppText>
                  </View>
                  <View style={stylesheet.itemActions}>
                    <AppText size="xs" weight="semibold">
                      {entry.billable
                        ? formatMoney(billableValue(entry))
                        : "Non-billable"}
                    </AppText>
                    <Button
                      compact
                      variant="outline"
                      onPress={() =>
                        router.push(`/time-entries/${entry.id}` as never)
                      }
                    >
                      <ButtonText>View</ButtonText>
                    </Button>
                  </View>
                </View>
              ))}
            </Stack>
          )}
        </Card>

        <Card gap="md">
          <SectionHeader
            title="Related invoices"
            description="Invoices that include time from this task."
          />
          {relatedInvoices.length === 0 ? (
            <EmptyState title="No invoices include this task's time yet." />
          ) : (
            <Stack gap="sm">
              {relatedInvoices.map((invoice) => (
                <View key={invoice.id} style={stylesheet.itemRow}>
                  <View style={stylesheet.itemMain}>
                    <AppText weight="semibold">
                      {invoice.invoice_number}
                    </AppText>
                    <AppText size="xs">
                      {invoice.status} - due {invoice.due_date ?? "not set"}
                    </AppText>
                  </View>
                  <View style={stylesheet.itemActions}>
                    <AppText size="xs" weight="semibold">
                      {formatMoney(invoice.total_cents)}
                    </AppText>
                    <Button
                      compact
                      variant="outline"
                      onPress={() =>
                        router.push(`/invoices/${invoice.id}` as never)
                      }
                    >
                      <ButtonText>View</ButtonText>
                    </Button>
                  </View>
                </View>
              ))}
            </Stack>
          )}
        </Card>
      </View>

      <View style={stylesheet.actions}>
        <Button
          align="start"
          variant="outline"
          onPress={() => router.push("/tasks" as never)}
        >
          <ButtonText>Back to tasks</ButtonText>
        </Button>
        {project ? (
          <Button
            align="start"
            variant="outline"
            onPress={() => router.push(`/projects/${project.id}` as never)}
          >
            <ButtonText>View project</ButtonText>
          </Button>
        ) : null}
      </View>
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    summaryGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    detailGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    actionPanel: {
      gap: spacing.m,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.background,
    },
    formGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
    },
    field: {
      minWidth: 160,
      flex: 1,
      gap: spacing.xs,
    },
    fullWidth: {
      flexBasis: "100%",
    },
    noteBlock: {
      gap: spacing.xs,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.surface,
    },
    itemRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.m,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.background,
    },
    itemMain: {
      flex: 1,
      minWidth: 220,
      gap: spacing.xs,
    },
    itemActions: {
      alignItems: "flex-end",
      gap: spacing.s,
    },
    actions: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
    },
  });
